import React, { useState, useRef, useEffect } from 'react';

interface MessageInputProps {
  /**
   * Callback when message is sent
   */
  onSendMessage: (content: string, attachments?: File[]) => void;

  /**
   * Callback when voice message is sent
   */
  onSendVoiceMessage?: (audioBlob: Blob, duration: number) => void;

  /**
   * Is currently sending a message
   */
  isSending?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;
}

/**
 * MessageInput Component
 * Input area for composing and sending messages
 */
const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onSendVoiceMessage,
  isSending = false,
  placeholder = 'Type a message...',
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformBars, setWaveformBars] = useState<number[]>(Array(20).fill(0.3));
  const [isDraggingOutside, setIsDraggingOutside] = useState(false);

  // Refs for recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const waveformTimerRef = useRef<NodeJS.Timeout | null>(null);
  const recordButtonRef = useRef<HTMLButtonElement>(null);
  const recordStartTimeRef = useRef<number>(0);

  /**
   * Handle send message
   */
  const handleSend = () => {
    if (message.trim() || attachments.length > 0) {
      onSendMessage(message.trim(), attachments);
      setMessage('');
      setAttachments([]);
    }
  };

  /**
   * Handle key press (Enter to send, Shift+Enter for new line)
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAttachments([...attachments, ...Array.from(files)]);
    }
  };

  /**
   * Remove attachment
   */
  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Common emojis for quick access
   */
  const quickEmojis = ['😊', '😂', '❤️', '👍', '🎉', '🔥', '✨', '👏'];

  /**
   * Format recording time as HH:MM:SS
   */
  const formatRecordingTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Start voice recording
   */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      recordStartTimeRef.current = Date.now();
      setRecordingTime(0);

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordStartTimeRef.current) / 1000);
        setRecordingTime(elapsed);
      }, 1000);

      // Animate waveform
      waveformTimerRef.current = setInterval(() => {
        setWaveformBars(Array(20).fill(0).map(() => 0.2 + Math.random() * 0.8));
      }, 100);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to access microphone. Please check your permissions.');
    }
  };

  /**
   * Stop recording and send
   */
  const stopRecordingAndSend = () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return;
    }

    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const duration = recordingTime;

      if (onSendVoiceMessage) {
        onSendVoiceMessage(audioBlob, duration);
      }

      // Cleanup
      cleanupRecording();
    };

    mediaRecorderRef.current.stop();
    mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
  };

  /**
   * Cancel recording
   */
  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    cleanupRecording();
  };

  /**
   * Cleanup recording state
   */
  const cleanupRecording = () => {
    setIsRecording(false);
    setRecordingTime(0);
    setWaveformBars(Array(20).fill(0.3));
    setIsDraggingOutside(false);
    audioChunksRef.current = [];

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    if (waveformTimerRef.current) {
      clearInterval(waveformTimerRef.current);
      waveformTimerRef.current = null;
    }
  };

  /**
   * Handle mouse/touch move for drag-to-cancel
   */
  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isRecording || !recordButtonRef.current) return;

    const rect = recordButtonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distance = Math.sqrt(Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2));

    // If dragged more than 80px from center, mark as cancel
    setIsDraggingOutside(distance > 80);
  };

  /**
   * Handle mouse/touch up for recording
   */
  const handleRecordEnd = () => {
    if (!isRecording) return;

    if (isDraggingOutside) {
      cancelRecording();
    } else {
      stopRecordingAndSend();
    }
  };

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      cleanupRecording();
    };
  }, []);

  return (
    <div className="flex-shrink-0 flex flex-col w-full bg-white border-t border-[#E5E7EB] shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.03)]">
      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="flex flex-row gap-2 p-4 pb-0 overflow-x-auto">
          {attachments.map((file, index) => (
            <div
              key={index}
              className="relative flex flex-row items-center gap-2 px-3 py-2 bg-[#F1F3F4] rounded-lg"
            >
              {/* File icon */}
              <div className="w-8 h-8 flex items-center justify-center bg-white rounded">
                {file.type.startsWith('image/') ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="12" height="12" rx="1" stroke="#015FA6" strokeWidth="1.5"/>
                    <circle cx="5.5" cy="5.5" r="1" fill="#015FA6"/>
                    <path d="M14 10L11 7L3 15" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1H4C3.73478 1 3.48043 1.10536 3.29289 1.29289C3.10536 1.48043 3 1.73478 3 2V14C3 14.2652 3.10536 14.5196 3.29289 14.7071C3.48043 14.8946 3.73478 15 4 15H12C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V6L9 1Z" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 1V6H13" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>

              {/* File info */}
              <div className="flex flex-col">
                <p className="font-manrope font-medium text-xs text-[#1A1A1A] max-w-[150px] truncate">
                  {file.name}
                </p>
                <p className="font-manrope font-normal text-xs text-[#757575]">
                  {formatFileSize(file.size)}
                </p>
              </div>

              {/* Remove button */}
              <button
                onClick={() => handleRemoveAttachment(index)}
                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-[#EDEDED] transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 3L3 9M3 3L9 9" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Emoji picker */}
      {showEmojiPicker && (
        <div className="absolute bottom-20 left-4 p-3 bg-white border border-[#EDEDED] rounded-lg shadow-lg">
          <div className="flex flex-row gap-2">
            {quickEmojis.map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  setMessage(message + emoji);
                  setShowEmojiPicker(false);
                }}
                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-[#F1F3F4] rounded transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area or Voice recording UI */}
      {isRecording ? (
        /* Voice Recording UI */
        <div
          className="flex flex-row items-center justify-center gap-4 p-6 relative"
          onMouseMove={(e) => handleDragMove(e.clientX, e.clientY)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientX, e.touches[0].clientY)}
          onMouseUp={handleRecordEnd}
          onTouchEnd={handleRecordEnd}
        >
          {/* Timer */}
          <div className="font-manrope font-semibold text-lg text-[#1A1A1A]">
            {formatRecordingTime(recordingTime)}
          </div>

          {/* Waveform visualization */}
          <div className="flex flex-row items-center gap-0.5 h-10">
            {waveformBars.map((height, index) => (
              <div
                key={index}
                className="w-1 bg-[#DC0000] rounded-full transition-all duration-100"
                style={{ height: `${height * 40}px` }}
              />
            ))}
          </div>

          {/* Recording button with red gradient */}
          <button
            ref={recordButtonRef}
            className="relative w-[55px] h-[55px] flex items-center justify-center rounded-full bg-[#DC0000] shadow-lg flex-shrink-0 cursor-pointer"
            style={{
              boxShadow: isDraggingOutside
                ? '0 0 0 0 rgba(220, 0, 0, 0)'
                : '0 0 0 10px rgba(220, 0, 0, 0.1), 0 0 0 20px rgba(220, 0, 0, 0.05)',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="9" y="2" width="6" height="12" rx="3" stroke="white" strokeWidth="2"/>
              <path d="M5 10V12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12V10" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 19V22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M8 22H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Cancel instruction */}
          <div className={`absolute bottom-0 left-0 right-0 text-center pb-2 transition-opacity duration-200 ${isDraggingOutside ? 'opacity-100' : 'opacity-60'}`}>
            <p className="font-manrope font-medium text-xs text-[#DC0000]">
              {isDraggingOutside ? 'Release to cancel' : 'Release outside of circle to cancel'}
            </p>
          </div>
        </div>
      ) : (
        /* Normal Input UI */
        <div className="p-4">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />

          {/* Contained input box */}
          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl focus-within:ring-2 focus-within:ring-[#015FA6]/20 focus-within:border-[#015FA6] transition-all duration-200">
            {/* Textarea */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={isSending}
              className="w-full px-4 pt-3 pb-2 font-manrope font-normal text-sm text-[#1A1A1A] bg-transparent border-none resize-none focus:outline-none placeholder:text-[#9CA3AF]"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '120px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
              }}
            />

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between px-3 pb-2">
              {/* Left: Emoji + Attach */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#EDEDED] transition-colors"
                  aria-label="Add emoji"
                  disabled={isSending}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8" stroke="#9CA3AF" strokeWidth="1.5"/>
                    <circle cx="7" cy="8" r="1" fill="#9CA3AF"/>
                    <circle cx="13" cy="8" r="1" fill="#9CA3AF"/>
                    <path d="M6.5 12C6.5 12 7.5 14 10 14C12.5 14 13.5 12 13.5 12" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#EDEDED] transition-colors"
                  aria-label="Attach file"
                  disabled={isSending}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.44 11.05L12.25 20.24C11.1242 21.3658 9.59718 21.9983 8.005 21.9983C6.41282 21.9983 4.88584 21.3658 3.76 20.24C2.63416 19.1142 2.00166 17.5872 2.00166 15.995C2.00166 14.4028 2.63416 12.8758 3.76 11.75L12.33 3.18C13.0806 2.42944 14.0991 2.00558 15.1615 2.00558C16.2239 2.00558 17.2424 2.42944 17.993 3.18C18.7436 3.93056 19.1674 4.94908 19.1674 6.01148C19.1674 7.07388 18.7436 8.0924 17.993 8.84296L9.41 17.41C9.03472 17.7853 8.52544 17.9972 7.995 17.9972C7.46456 17.9972 6.95528 17.7853 6.58 17.41C6.20472 17.0347 5.99283 16.5254 5.99283 15.995C5.99283 15.4646 6.20472 14.9553 6.58 14.58L15.07 6.1" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Right: Send or Mic */}
              {message.trim() || attachments.length > 0 ? (
                <button
                  onClick={handleSend}
                  disabled={isSending}
                  className="w-8 h-8 flex items-center justify-center bg-[#015FA6] rounded-lg hover:bg-[#014580] shadow-sm active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Send message"
                >
                  {isSending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 2L9 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18 2L12 18L9 11L2 8L18 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ) : (
                <button
                  onMouseDown={startRecording}
                  onTouchStart={startRecording}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#EDEDED] transition-colors"
                  aria-label="Record voice message"
                  disabled={isSending}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="9" y="2" width="6" height="12" rx="3" stroke="#9CA3AF" strokeWidth="2"/>
                    <path d="M5 10V12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12V10" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 19V22" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 22H16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Hint text */}
          <p className="text-[10px] text-[#9CA3AF] text-center mt-2">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
};

export default MessageInput;
